import React from 'react';
import { Facebook, Instagram, Linkedin, Youtube, MessageCircle } from 'lucide-react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Separator } from './ui/separator';
import TikTokIcon from './TikTokIcon';
import { Helmet } from 'react-helmet-async';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Shippzy Inc.",
    "image": "https://customer-assets.emergentagent.com/job_angry-ellis-7/artifacts/rd8nrfx9_IMG_7688.png",
    "telephone": "+1-437-616-0758",
    "email": "ship@shippzy.ca",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "140 Advance Blvd, Unit 7",
      "addressLocality": "Brampton",
      "addressRegion": "ON",
      "postalCode": "L6T 4Z8",
      "addressCountry": "CA"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "43.693950",
      "longitude": "-79.693070"
    },
    "url": "https://www.shippzy.ca",
    "areaServed": "Canada",
    "priceRange": "$$",
    "openingHours": "Mo-Su 00:00-23:59",
    "sameAs": [
      "https://www.facebook.com/share/1Fpxae5bzy/?mibextid=wwXIfr",
      "https://instagram.com/shippzycouriers?igshid=YmMyMTA2M2Y=",
      "https://www.tiktok.com/@shippzycouriers?_r=1&_t=zs-94si6mazl6l",
      "https://www.linkedin.com/company/shippzy/",
      "https://youtube.com/@shippzycouriers?si=8LciIaaIDdOqxwDN"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Shipping Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "International Shipping",
            "description": "Affordable international courier services to 185+ countries"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Courier Services",
            "description": "Fast and reliable courier services from Canada"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Relocation Shipping",
            "description": "Complete household and relocation shipping services"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Parcel Delivery",
            "description": "Door-to-door parcel delivery with tracking"
          }
        }
      ]
    }
  };

  const footerLinks = {
    services: [
      { name: 'International Shipping', href: '#' },
      { name: 'Shipping to India', href: '/shipping-to-india-from-canada' },
      { name: 'Supplements Shipping', href: '/shipping-supplements-protein' },
      { name: 'Electronics Shipping', href: '/electronics-battery-shipping' },
      { name: 'Shipping from Brampton', href: '/international-shipping-brampton' },
      { name: 'Courier Toronto', href: '/international-courier-toronto' },
      { name: 'Shipping Cost', href: '/shipping-cost-canada-to-india' },
      { name: 'Cheap Shipping', href: '/cheap-shipping-canada-to-india' },
      { name: 'Relocation Shipping', href: '/relocation-shipping-canada-to-india' },
      { name: 'International Relocation', href: '/relocation' },
      { name: 'Drop-Off Locations', href: '/drop-off-locations' },
      { name: 'Track Shipment', href: '/track-shipment' }
    ],
    destinations: [
      { name: 'All Destinations', href: '/shipping-destinations' },
      { name: 'Canada to India', href: '/shipping-to-india-from-canada' },
      { name: 'Canada to UK', href: '/shipping-canada-to-uk' },
      { name: 'Canada to UAE/Dubai', href: '/shipping-canada-to-uae' },
      { name: 'Documents to India', href: '/documents-to-india-from-canada' },
      { name: 'Student Shipping', href: '/student-shipping-canada-to-india' },
      { name: 'Relocation Services', href: '/relocation-shipping-canada-to-india' }
    ],
    canadianCities: [
      { name: 'Toronto', href: '/international-courier-toronto' },
      { name: 'Brampton', href: '/international-shipping-brampton' },
      { name: 'Mississauga', href: '/international-shipping-mississauga' },
      { name: 'Surrey', href: '/international-shipping-surrey' },
      { name: 'Vancouver', href: '/international-shipping-vancouver' },
      { name: 'Nova Scotia', href: '/international-shipping-nova-scotia' }
    ],
    support: [
      { name: 'Track Shipment', href: '/track-shipment' },
      { name: 'Blog', href: '/blog' },
      { name: 'Shipping Cost', href: '/shipping-cost-canada-to-india' },
      { name: 'Calculate Volume Weight', href: '/actual-weight-vs-volumetric-weight' },
      { name: 'FAQs', href: '/faq' },
      { name: 'Terms of Service', href: '/terms-conditions' },
      { name: 'Privacy Policy', href: '/privacy-policy' }
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy-policy' },
      { name: 'Terms & Conditions', href: '/terms-conditions' },
      { name: 'Customs Declaration Form', href: '/customs-declaration' }
    ],
    forms: [
      { name: 'Customs Declaration Form', href: '/customs-declaration' },
      { name: 'Sender–Receiver Form', href: '/sender-receiver-form' }
    ]
  };

  const socialLinks = [
    { icon: Facebook, url: 'https://www.facebook.com/share/1Fpxae5bzy/?mibextid=wwXIfr', label: 'Facebook' },
    { icon: Instagram, url: 'https://instagram.com/shippzycouriers?igshid=YmMyMTA2M2Y=', label: 'Instagram' },
    { icon: TikTokIcon, url: 'https://www.tiktok.com/@shippzycouriers?_r=1&_t=zs-94si6mazl6l', label: 'TikTok' },
    { icon: Linkedin, url: 'https://www.linkedin.com/company/shippzy/', label: 'LinkedIn' },
    { icon: Youtube, url: 'https://youtube.com/@shippzycouriers?si=8LciIaaIDdOqxwDN', label: 'YouTube' }
  ];

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>
      <footer className="bg-black text-gray-300 border-t border-gray-800">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_angry-ellis-7/artifacts/rd8nrfx9_IMG_7688.png" 
                alt="Shippzy Logo" 
                className="h-12 w-12 rounded-full"
              />
              <div className="ml-3">
                <h3 className="text-2xl font-bold text-white">SHIPPZY</h3>
                <p className="text-xs text-yellow-400">Ship with Shippzy</p>
              </div>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Canada's trusted international courier service. Compare carriers, save UPTO 75%, and ship to 185+ countries worldwide.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-red-500" />
                <span>+1 (437) 616-0758</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-red-500" />
                <span>ship@shippzy.ca</span>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <MapPin className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span>140 Advance Blvd, Unit 7, Brampton, ON L6T 4Z8</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-3 mt-6">
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-10 h-10 bg-gray-900 hover:bg-gradient-to-r hover:from-red-600 hover:to-yellow-500 rounded-full flex items-center justify-center transition-all duration-200"
                  >
                    <IconComponent className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Services</h4>
            <ul className="space-y-2">
              {footerLinks.services.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Destinations Links */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Destinations</h4>
            <ul className="space-y-2">
              {footerLinks.destinations.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Support</h4>
            <ul className="space-y-2">
              {footerLinks.support.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Documents */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Legal Documents</h4>
            <ul className="space-y-2">
              {footerLinks.legal.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Shipping Forms */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Shipping Forms</h4>
            <ul className="space-y-2">
              {footerLinks.forms.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <Separator className="bg-gray-900" />

      {/* Bottom Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
          <p>© {currentYear} Shippzy Inc. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="/privacy-policy" className="hover:text-red-500 transition-colors">Privacy Policy</a>
            <a href="/terms-conditions" className="hover:text-red-500 transition-colors">Terms of Service</a>
            <a href="/customs-declaration" className="hover:text-red-500 transition-colors">Customs Declaration</a>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;